import { Document, LiveQuery, Subscription } from '@dittolive/ditto'
import { useEffect, useRef, useState } from 'react'

import useCurrentApp from './useCurrentApp'
import useCurrentPage from './useCurrentPage'
import useDittoClient from './useDittoClient'
import useSearch from './useSearch'
import { stopLiveQuery } from './utils'

type Result = {
  /** Application issues */
  issues: Document[]
  /** Remove issue callback. */
  remove: (documentId: string) => Promise<boolean | undefined>
}

type Props = {
  pageSize: number
  /** App ID */
  appId: string
}

/** This hook queries the current application's issues page. */
const useIssues = ({ pageSize, appId }: Props): Result => {
  const { organizationId } = useCurrentApp(appId)
  const client = useDittoClient()
  const [issues, setIssues] = useState<Document[]>([])
  const issuesSub = useRef<Subscription | undefined>()
  const issuesQuery = useRef<LiveQuery | undefined>()
  const currentPage = useCurrentPage()
  const search = useSearch()

  const getFilter = () => {
    return search
      ? //FIXME: it might be enough to filter by the app ID in the future, given we agreed on it's uniqueness across all systems
        `organizationId == '${organizationId}' && appId == '${appId}' && regex(title, '${search}')`
      : `organizationId == '${organizationId}' && appId == '${appId}'`
  }

  useEffect(() => {
    if (client.store) {
      stopLiveQuery(issuesQuery)
      const filter = getFilter()
      const collectionName = 'issues'
      const offset = pageSize * (currentPage - 1)
      const op = client.store
        .collection(collectionName)
        .find(filter)
        .sort('createdAt', 'descending')
        .offset(offset)
        .limit(pageSize)
      issuesSub.current = op.subscribe()
      issuesQuery.current = op.observeLocal(setIssues)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, organizationId, appId, currentPage])

  useEffect(() => {
    return () => {
      stopLiveQuery(issuesQuery)
      issuesSub.current?.cancel()
    }
  }, [])

  /** WIP: this is to clean up the issues in the dev mode, should be removed */
  const remove = async (id: string): Promise<boolean | undefined> =>
    await client.store.collection('issues')?.findByID(id).remove()

  return {
    issues,
    remove,
  }
}

export default useIssues
